import ManaWorld from "../../loop/ManaWorld";
import TickingObject, { TickingObjectOptions } from "../../modules/TickingObject";
import ManaParticle from "../ManaParticle";
import NodeCategories from "./NodeCategories";

import { v4 as uuidv4 } from 'uuid'
import { Canvas, IEvent, Object } from "fabric/fabric-impl";
import { keyboard } from "../../../input/keyboard";
import { Subject } from "rxjs";


export type ManaNodeOptions = {
	uuid?: string,
	cooldown?: number
	cost?: number
	size?: number
	baseSize?: number
	world?: ManaWorld
} & TickingObjectOptions

export default class ManaNode extends TickingObject<Object, Canvas> {
	uuid

	world

	cooldown
	connections = [] as ManaNode[]
	particles = [] as ManaParticle[]
	maxParticleIndicationCooldown = 0.225
	particleIndicationCooldown = 0

	cost
	size
	symbol

	onResize$ = new Subject<number>();

	static nodeName: string
	static nodeDesc: string
	static nodeIcon: string
	static category: NodeCategories

	static baseSize = 40
	static nodeType = 'ManaNode'

	constructor({ uuid = uuidv4(), world = undefined as (undefined | ManaWorld), cooldown = 0, cost = 0, size = ManaNode.baseSize, ...rest }: ManaNodeOptions) {
		super(rest)

		this.uuid = uuid
		this.cooldown = cooldown
		this.cost = cost
		this.size = size
		this.symbol = this.getSymbol()
		this.world = world
	}

	getTooltip(): any {
		return undefined
	}

	getSymbol() {
		return ''
	}

	onScroll(dir: number) {
		const amt = dir * (keyboard.shiftDown ? 1 : 10)
		this.setSize(this.size - amt)
	}

	setSize(size: number) {
		this.size = size;

		this.onResize$.next(size)
	}

	getBaseSize() {
		return ManaNode.baseSize
	}

	getSizeAmp() {
		const area = Math.PI * (this.size ** 2)
		const baseArea = Math.PI * (this.getBaseSize() ** 2)
		return (area / baseArea)
	}

	getAmp(particle: ManaParticle) {
		return 1 + (this.getSizeAmp() - 1) + (particle.amp - 1)
	}

	getParticleTrueCost(cost: number, particle: ManaParticle) {
		return (cost * (1 + (particle.costModifier - 1) + (this.getSizeAmp() - 1))) + ((cost > 0) ? particle.costAddition : 0)
	}

	addParticle(particle: ManaParticle) {
		this.particles.push(particle)

		const fn = async () => {

			const trueCost = this.getParticleTrueCost(this.cost, particle)

			if (particle.source.mana >= trueCost) {

				particle.source.consumeMana(trueCost)
				const advance = await this.act(particle)

				if (advance) {
					this.advanceParticle(particle)
				} else {
					this.errorParticle(particle)
				}
			} else {
				this.errorParticle(particle)
			}
		}

		if (this.cooldown) {
			setTimeout(fn, this.cooldown)
		} else {
			fn()
		}

	}

	removeParticle(particle: ManaParticle) {
		particle.dispose()
		this.particles = this.particles.filter(p => p !== particle)
		this.particleIndicationCooldown = this.maxParticleIndicationCooldown
	}

	errorParticle(particle: ManaParticle) {
		const errorRoutes = this.connections.filter(connection => connection.allowErrorEntry(particle))

		const spawns = particle.split(errorRoutes.length)

		errorRoutes.forEach((connection, index) => {
			connection.addParticle(spawns[index])
		})

		this.removeParticle(particle)
	}

	getViableRoutes(particle: ManaParticle) {
		return (
			this.connections.filter(
				connection => connection.allowEntry(particle)
			)
		)
	}

	advanceParticle(particle: ManaParticle): void {
		const viableRoutes = this.getViableRoutes(particle)

		const splitParticles = particle.split(viableRoutes.length)

		if (viableRoutes.length) {
			this.removeParticle(particle)

			viableRoutes.forEach((route, index) => {
				route.addParticle(splitParticles[index])
			})
		} else {
			this.errorParticle(particle)
		}

	}

	allowEntry(particle: ManaParticle): boolean {
		return true
	}

	async act(particle: ManaParticle) { return true }

	save() {
		return {
			position: { x: this.position.x, y: this.position.y },
			type: (this.constructor as any).nodeType,
			size: this.size
		}
	}

	static load(obj: any) {
		const { position, ...rest } = obj
		const instance = new this(rest)

		instance.position.x = position.x
		instance.position.y = position.y

		return instance
	}

	addGraphics(canvas: Canvas) {
		super.addGraphics(canvas)

		if (this.graphics) {

			const g = this.graphics
			canvas.add(g)
			this.removeCallback = () => canvas.remove(g)

			this.graphics.on('mousewheel', (event: IEvent) => {
				if (canvas.getActiveObject() === this.graphics) {
					const nativeEvent = event.e as any
					const dir = nativeEvent.deltaY > 0 ? 1 : -1
					this.onScroll(dir)

					nativeEvent.stopPropagation()
					nativeEvent.preventDefault()
				}
			})
		}
	}

	tick(delta: number) {
		super.tick(delta)
		if (this.particleIndicationCooldown > 0) {
			this.particleIndicationCooldown = Math.max(0, this.particleIndicationCooldown - delta)
		}
	}

	allowErrorEntry(particle: ManaParticle) {
		return false
	}

	dispose() {
		super.dispose()
		this.particles.forEach(particle => particle.dispose())
		this.world?.remove(this)
	}
}